import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { 
  getCampusLeadApps, updateCampusLeadStatus, 
  getSwagClaims, updateSwagClaimStatus, 
  getReferralLeaderboard 
} from '../services/api';

export default function ReferralDashboardPage() {
  const [tabIndex, setTabIndex] = useState(0);
  
  // States
  const [loading, setLoading] = useState(true);
  const [campusLeads, setCampusLeads] = useState<any[]>([]);
  const [swagClaimsData, setSwagClaimsData] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [selectedSwag, setSelectedSwag] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [tabIndex]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (tabIndex === 0) {
        const res = await getCampusLeadApps('all');
        if (res.success) setCampusLeads(res.data);
      } else if (tabIndex === 1) {
        const res = await getReferralLeaderboard();
        if (res.success) setLeaderboard(res.data);
      } else if (tabIndex === 2) {
        const res = await getSwagClaims();
        if (res.success) setSwagClaimsData(res.data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  const handleUpdateCampusLead = async (status: string) => {
    try {
      if (!selectedApp) return;
      await updateCampusLeadStatus(selectedApp._id, status, adminNotes);
      setSuccess(`Application marked as ${status}`);
      setSelectedApp(null);
      loadData();
    } catch (err) {
      console.log(err);
      setError('Error updating application status');
    }
  };

  const handleUpdateSwag = async (status: string) => {
    try {
      if (!selectedSwag) return;
      await updateSwagClaimStatus(selectedSwag.user._id, selectedSwag.claim._id, status, adminNotes);
      setSuccess(`Swag claim marked as ${status}`);
      setSelectedSwag(null);
      loadData();
    } catch (err) {
      console.log(err);
      setError('Error updating swag claim status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': case 'shipped': case 'delivered': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  // Convert nested swag claims to flat list
  const getFlatSwagsList = () => {
    const list: any[] = [];
    swagClaimsData.forEach(r => {
      r.swagClaims.forEach((claim: any) => {
        list.push({ user: r.user, validReferrals: r.validReferrals, claim });
      });
    });
    return list;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Growth & Referral Dashboard</Typography>
      
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        <Tab label="Campus Leads" />
        <Tab label="Referral Leaderboard" />
        <Tab label="Swag Claims" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tabIndex === 0 && (
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant Page</TableCell>
                    <TableCell>College / City</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applied At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campusLeads.map(app => (
                    <TableRow key={app._id}>
                      <TableCell>
                        <Typography variant="subtitle2">{app.name}</Typography>
                        <Typography variant="body2" color="textSecondary">{app.user?.email}</Typography>
                      </TableCell>
                      <TableCell>{app.collegeName} ({app.city})</TableCell>
                      <TableCell>
                        <Chip label={app.status.toUpperCase()} color={getStatusColor(app.status)} size="small" />
                      </TableCell>
                      <TableCell>{new Date(app.appliedAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button variant="outlined" size="small" onClick={() => {
                          setSelectedApp(app);
                          setAdminNotes(app.adminNotes || '');
                        }}>Review</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {campusLeads.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center">No applications found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}

          {tabIndex === 1 && (
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Valid Referrals</TableCell>
                    <TableCell>Total Refrrals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaderboard.map((r, i) => (
                    <TableRow key={r._id}>
                      <TableCell>#{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{r.user?.firstName} {r.user?.lastName}</Typography>
                        <Typography variant="body2" color="textSecondary">{r.user?.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={r.validReferrals} color="success" size="small" />
                      </TableCell>
                      <TableCell>{r.totalReferrals}</TableCell>
                    </TableRow>
                  ))}
                  {leaderboard.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center">No referrals found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}

          {tabIndex === 2 && (
            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Valid Referrals</TableCell>
                    <TableCell>Swag Requested</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFlatSwagsList().map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography variant="subtitle2">{item.user?.firstName} {item.user?.lastName}</Typography>
                        <Typography variant="body2" color="textSecondary">{item.user?.email}</Typography>
                      </TableCell>
                      <TableCell>{item.validReferrals}</TableCell>
                      <TableCell>{item.claim.swagType}</TableCell>
                      <TableCell>
                        <Chip label={item.claim.status.toUpperCase()} color={getStatusColor(item.claim.status)} size="small" />
                      </TableCell>
                      <TableCell>{new Date(item.claim.appliedAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button variant="outlined" size="small" onClick={() => {
                          setSelectedSwag(item);
                          setAdminNotes(item.claim.notes || '');
                        }}>Manage</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFlatSwagsList().length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center">No swag claims found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      {/* Campus Lead Dialog */}
      <Dialog open={!!selectedApp} onClose={() => setSelectedApp(null)} maxWidth="sm" fullWidth>
        {selectedApp && (
          <>
            <DialogTitle>Review Campus Lead Application</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box><Typography variant="subtitle2">Name:</Typography> <Typography>{selectedApp.name}</Typography></Box>
                <Box><Typography variant="subtitle2">College & Year:</Typography> <Typography>{selectedApp.collegeName} ({selectedApp.courseYear})</Typography></Box>
                <Box><Typography variant="subtitle2">City:</Typography> <Typography>{selectedApp.city}</Typography></Box>
                <Box><Typography variant="subtitle2">Why Lead:</Typography> <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap'}}>{selectedApp.whyLead}</Typography></Box>
                {selectedApp.societiesClubs && <Box><Typography variant="subtitle2">Societies:</Typography> <Typography variant="body2">{selectedApp.societiesClubs}</Typography></Box>}
                {selectedApp.linkedin && <Box><Typography variant="subtitle2">LinkedIn:</Typography> <Typography variant="caption">{selectedApp.linkedin}</Typography></Box>}
                
                <Box sx={{ mt: 2 }}>
                  <TextField 
                    fullWidth label="Admin Notes (Internal)" multiline rows={3} 
                    value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} 
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedApp(null)}>Cancel</Button>
              <Button onClick={() => handleUpdateCampusLead('rejected')} color="error">Reject</Button>
              <Button onClick={() => handleUpdateCampusLead('approved')} color="success" variant="contained">Approve</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Swag Claim Dialog */}
      <Dialog open={!!selectedSwag} onClose={() => setSelectedSwag(null)} maxWidth="sm" fullWidth>
        {selectedSwag && (
          <>
            <DialogTitle>Review Swag Claim</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2">User:</Typography> 
              <Typography>{selectedSwag.user?.firstName} {selectedSwag.user?.lastName} ({selectedSwag.user?.email})</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Valid Referrals:</Typography> 
              <Typography color={selectedSwag.validReferrals >= 100 ? 'success.main' : 'error.main'}>{selectedSwag.validReferrals} (100 required)</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Swag Item:</Typography> 
              <Typography>{selectedSwag.claim.swagType}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Shipping Address:</Typography> 
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{selectedSwag.claim.shippingAddress || 'Not provided'}</Typography>
              
              <TextField 
                fullWidth label="Admin Notes / Tracking Number" multiline rows={2} 
                value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} 
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedSwag(null)}>Cancel</Button>
              <Button onClick={() => handleUpdateSwag('rejected')} color="error">Reject</Button>
              <Button onClick={() => handleUpdateSwag('approved')} color="primary">Approve</Button>
              <Button onClick={() => handleUpdateSwag('shipped')} color="info">Mark Shipped</Button>
              <Button onClick={() => handleUpdateSwag('delivered')} color="success" variant="contained">Delivered</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

    </Box>
  );
}
